/**
 * Step one of government-identity verification: the official agency address.
 *
 * Ported from the le-portal-ui-v3 prototype's `agency-email` step (src/LoginFlow.tsx).
 * The address is asked for twice because a typo here is silent — the code goes to the
 * mistyped mailbox and the user is left waiting for mail that cannot arrive.
 *
 * Presentational: the value lives in `GovIdVerification` so that returning from the code
 * screen does not clear what was typed.
 */
import type { FormEvent } from 'react';
import {
  Button,
  Card,
  Divider,
  Field,
  Input,
  MessageBar,
  mergeClasses,
  MessageBarBody,
  Spinner,
  Title2,
} from '@fluentui/react-components';
import { PersonCircleRegular } from '@fluentui/react-icons';
import { agencyEmailsMatch, isValidAgencyEmail, normalizeEmail } from '../../govIdVerification';
import { useLoginStyles } from '../loginStyles';

export interface GovIdPageProps {
  email: string;
  confirmEmail: string;
  /** Whether the request for a code is in flight. */
  isSending: boolean;
  /** Why the last request for a code failed, or null when none has. */
  errorMessage: string | null;
  onEmailChange: (value: string) => void;
  onConfirmEmailChange: (value: string) => void;
  /** Called once both fields hold the same valid address; the server sends the code. */
  onSendCode: () => void;
}

export function GovIdPage({
  email,
  confirmEmail,
  isSending,
  errorMessage,
  onEmailChange,
  onConfirmEmailChange,
  onSendCode,
}: GovIdPageProps) {
  const loginStyles = useLoginStyles();

  // Only complain about what the user has actually typed: an untouched field is not an
  // error, it is a field they have not reached yet.
  const emailIsMalformed = email.length > 0 && !isValidAgencyEmail(email);
  const confirmationDiffers =
    confirmEmail.length > 0 && normalizeEmail(email) !== normalizeEmail(confirmEmail);
  const canSendCode = agencyEmailsMatch(email, confirmEmail);

  // The button is disabled in exactly these cases, so a browser will not submit through it
  // anyway; the guard is here because Enter is a second way in and the two should not be
  // able to disagree about when the address is ready to send.
  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!canSendCode || isSending) {
      return;
    }

    onSendCode();
  };

  return (
    <Card appearance="outline" className={mergeClasses(loginStyles.card, loginStyles.cardNarrow)}>
      <div className={loginStyles.stepHeading}>
        <div className={loginStyles.centeredHeading}>
          <span className={mergeClasses(loginStyles.headingIcon, loginStyles.centeredHeadingIcon)} aria-hidden="true">
            <PersonCircleRegular />
          </span>
          <Title2 as="h1" id="login-content-title" tabIndex={-1}>
            Confirm your official agency email
          </Title2>
          <p>
            This is a routine security check. You may need to confirm your official agency
            email every 24 hours, whether this is your first visit or you've used the portal
            before.
          </p>
        </div>
      </div>

      <Divider />

      {errorMessage !== null && (
        <MessageBar intent="error" layout="multiline">
          <MessageBarBody>{errorMessage}</MessageBarBody>
        </MessageBar>
      )}

      <form className={loginStyles.stepForm} noValidate onSubmit={handleSubmit}>
        <div className={loginStyles.formFields}>
          <Field
            label="Official agency email"
            required
            hint="We will send a verification code to this address."
            validationMessage={emailIsMalformed ? 'Enter a valid email address.' : undefined}
            validationState={emailIsMalformed ? 'error' : 'none'}
          >
            <Input
              aria-invalid={emailIsMalformed}
              type="email"
              value={email}
              placeholder="name@agency.gov"
              onChange={(_, data) => onEmailChange(data.value)}
            />
          </Field>
          <Field
            label="Confirm official agency email"
            required
            validationMessage={confirmationDiffers ? 'The email addresses do not match.' : undefined}
            validationState={confirmationDiffers ? 'error' : 'none'}
          >
            <Input
              aria-invalid={confirmationDiffers}
              type="email"
              value={confirmEmail}
              placeholder="name@agency.gov"
              onChange={(_, data) => onConfirmEmailChange(data.value)}
            />
          </Field>
        </div>

        <div className={loginStyles.actions}>
          <Button
            appearance="primary"
            type="submit"
            disabled={!canSendCode || isSending}
            icon={isSending ? <Spinner size="tiny" /> : undefined}
          >
            {isSending ? 'Sending code' : 'Send verification code'}
          </Button>
        </div>
      </form>
    </Card>
  );
}
